import { GridLayout, LayoutType } from './use-grid-layout';
import { clamp } from '@/utils/clamp';
import { map } from '@/utils/map';
import { norm } from '@/utils/norm';
import { distance } from '@/utils/distance';
import {
  DESKTOP_LAYOUT,
  MOBILE_LAYOUT,
  DESKTOP_COLUMNS,
  DESKTOP_ROWS,
  MOBILE_COLUMNS,
  MOBILE_ROWS,
} from './constants';

/**
 * Retrieves the column and row size for a specific layout
 */
export const getGridSizeByType = (type: LayoutType): [number, number] => {
  if (type === 'desktop') {
    return [DESKTOP_COLUMNS, DESKTOP_ROWS];
  }
  return [MOBILE_COLUMNS, MOBILE_ROWS];
};

/**
 * Retrieves the column and row index for a specific index and layout
 */
export const getCellInfoByIndex = (type: LayoutType, index: number): [number, number] => {
  const layout = type === 'desktop' ? DESKTOP_LAYOUT : MOBILE_LAYOUT;
  return layout[index] as [number, number];
};

/**
 * Calculate resistance based on current values and drag direction
 * Resistance gets multiplied with drag delta which results in friction at edges
 */
export const calculateResistance = (
  value: number,
  min: number,
  max: number,
  direction: number
): number => {
  if (min === max) {
    return 1;
  }

  const power = 3;
  const multiplier = 0.15;
  let resistance = 1;
  const n = norm(value, min, max);

  if (n > 1 && direction === -1) {
    resistance = (1.0 - (n - 1)) ** power * multiplier;
  } else if (n < 0 && direction === 1) {
    resistance = (1.0 - Math.abs(n)) ** power * multiplier;
  }

  return resistance;
};

/**
 * Based on a given index and layout information, return the correct scroll offset
 */
export const calculateScrollPosition = (
  index: number,
  gridLayout: GridLayout
): [x: number, y: number] => {
  const { cardWidth, cardHeight, gap, containerWidth, containerHeight, layout } = gridLayout;
  const [colIndex, rowIndex] = getCellInfoByIndex(layout, index);
  const baseX = colIndex * (cardWidth + gap);
  const baseY = rowIndex * (cardHeight + gap);
  const cx = baseX + cardWidth * 0.5;
  const cy = baseY + cardHeight * 0.5;
  const x = containerWidth * 0.5 - cx;
  const y = containerHeight * 0.5 - cy;

  return [x, y];
};

/**
 * Based on layout calculate the center of the grid and return the index from the layout array
 */
export const getCenterOfGridIndex = (layoutType: LayoutType) => {
  const layout = layoutType === 'desktop' ? DESKTOP_LAYOUT : MOBILE_LAYOUT;
  const columns = layoutType === 'desktop' ? DESKTOP_COLUMNS : MOBILE_COLUMNS;
  const rows = layoutType === 'desktop' ? DESKTOP_ROWS : MOBILE_ROWS;

  const centerColumnIndex = Math.round((columns + 1) / 2) - 1;
  const centerRowIndex = Math.round((rows + 1) / 2) - 1;
  const centerItemIndex = layout.findIndex(
    (d) => d[0] === centerColumnIndex && d[1] === centerRowIndex
  );

  // return 0 if index is not found in layout
  return centerItemIndex !== -1 ? centerItemIndex : 0;
};

/**
 * Based on a the scroll offset and layout information, figure out at which index we're at
 */
export const calculateGridIndex = (
  scrollX: number,
  scrollY: number,
  gridLayout: GridLayout
): number => {
  const {
    containerWidth,
    containerHeight,
    cardWidth,
    cardHeight,
    gridWidth,
    gridHeight,
    layout: layoutType,
  } = gridLayout;

  // correction to offset from center
  const correctionX = (containerWidth - cardWidth) * 0.5;
  const correctionY = (containerHeight - cardHeight) * 0.5;

  const [columns, rows] = getGridSizeByType(layoutType);

  const x = scrollX - correctionX;
  const y = scrollY - correctionY;
  const xEnd = cardWidth - gridWidth;
  const yEnd = cardHeight - gridHeight;

  const colIndex = clamp(map(x, 0, xEnd, 0, columns - 1), 0, columns - 1) || 0;
  const rowIndex = clamp(map(y, 0, yEnd, 0, rows - 1), 0, rows - 1) || 0;

  const layout = layoutType === 'desktop' ? DESKTOP_LAYOUT : MOBILE_LAYOUT;

  // search for cell in grid that's nearest to the current point
  const nearest = { index: -1, distance: Infinity };
  const p1 = { x: colIndex, y: rowIndex };
  layout.forEach(([c, r], i) => {
    const d = distance(p1, { x: c, y: r });
    if (d < nearest.distance) {
      nearest.index = i;
      nearest.distance = d;
    }
  });

  return nearest.index;
};
